import { z } from "zod";
import { customerOperationsAggregateSchema, customerOperationsCausationIdSchema, customerOperationsCorrelationIdSchema, customerOperationsEventIdSchema, customerOperationsOutcomeSchema, customerOperationsProducerSchema, customerOperationsReasonCodeSchema, customerOperationsSchemaVersionSchema, customerOperationsStageSchema, customerOperationsSubjectSchema, } from "./customer-operations-core.js";
z.config({ jitless: true });
function aggregateIdSourceFor(definition) {
    switch (definition.aggregateType) {
        case "PROFILE":
            return "customerId";
        case "BOOK":
            return "bookId";
        case "SOURCE":
        case "PRODUCTION_EVIDENCE":
        case "OUTBOX":
        case "PROJECTION":
            return "artifactId";
        case "GENERATION":
        case "RECONCILIATION":
            return "workflowId";
        case "ORDER":
        case "FULFILLMENT":
            return "orderId";
        case "SUPPORT_TICKET":
            return "ticketId";
        case "EXPORT":
            return definition.requiredSubjectIds.includes("artifactId") ? "artifactId" : null;
        default:
            return null;
    }
}
function defineEvent(definition) {
    return {
        ...definition,
        aggregateVersionRule: definition.aggregateType === null ? "FORBIDDEN" : "REQUIRED_POSITIVE",
        aggregateIdSource: aggregateIdSourceFor(definition),
    };
}
function deepFreeze(value) {
    if (value !== null && typeof value === "object" && !Object.isFrozen(value)) {
        for (const nested of Object.values(value))
            deepFreeze(nested);
        Object.freeze(value);
    }
    return value;
}
const succeeded = [{ outcome: "SUCCEEDED", reasonCodes: [], allowNoReason: true }];
const customerAccepted = [
    { outcome: "ACCEPTED", reasonCodes: ["CUSTOMER_REQUESTED"], allowNoReason: true },
];
const providerAccepted = [
    { outcome: "ACCEPTED", reasonCodes: ["PROVIDER_CONFIRMED"], allowNoReason: false },
];
const policyRejected = [
    { outcome: "REJECTED", reasonCodes: ["POLICY_DECISION"], allowNoReason: false },
];
const validationRejected = [
    { outcome: "REJECTED", reasonCodes: ["VALIDATION_REJECTED"], allowNoReason: false },
];
const conflictRejected = [
    { outcome: "REJECTED", reasonCodes: ["CONFLICT_DETECTED"], allowNoReason: false },
];
const systemFailed = [
    { outcome: "FAILED", reasonCodes: ["SYSTEM_FAILURE"], allowNoReason: false },
];
const validationFailed = [
    { outcome: "FAILED", reasonCodes: ["VALIDATION_REJECTED"], allowNoReason: false },
];
const providerFailed = [
    { outcome: "FAILED", reasonCodes: ["PROVIDER_REJECTED"], allowNoReason: false },
];
const deadLetterFailed = [
    { outcome: "FAILED", reasonCodes: ["DEAD_LETTER_REDRIVE"], allowNoReason: false },
];
const conflictFailed = [
    { outcome: "FAILED", reasonCodes: ["CONFLICT_DETECTED"], allowNoReason: false },
];
const observed = [{ outcome: "OBSERVED", reasonCodes: [], allowNoReason: true }];
const recovered = [
    { outcome: "RECOVERED", reasonCodes: ["RECONCILIATION_MATCHED"], allowNoReason: true },
];
const scheduled = [
    { outcome: "SCHEDULED", reasonCodes: ["RETRY_SCHEDULED"], allowNoReason: false },
];
const serverApi = ["CUSTOMER_API"];
const workflow = ["WORKFLOW"];
const supportApi = ["SUPPORT_API", "ADMIN_API"];
const provider = ["PROVIDER_WEBHOOK", "PROVIDER_RECONCILER"];
const awsCognito = ["COGNITO_TRIGGER"];
const awsNormalizer = ["AWS_EVENT_NORMALIZER"];
const recovery = ["OPERATIONS_RECOVERY"];
const customer = ["customerId"];
const customerBook = ["customerId", "bookId"];
const customerBookWorkflow = ["customerId", "bookId", "workflowId"];
const customerBookArtifact = ["customerId", "bookId", "artifactId"];
const customerOrder = ["customerId", "orderId"];
const ticket = ["ticketId"];
const workflowSubject = ["workflowId"];
const artifactSubject = ["artifactId"];
/**
 * This is intentionally a partial first catalog. New domains cross the typed expansion boundary by
 * adding a versioned definition here; no unregistered event or arbitrary payload is accepted.
 */
export const customerOperationsCatalogIdentity = deepFreeze({
    catalogVersion: "LC100_OPERATIONS_PARTIAL_V1",
    scope: "PARTIAL_FIRST_CATALOG",
    expansionBoundary: "ADDITIVE_VERSIONED_EVENT_DEFINITION",
    deferredFactGroups: [
        "CONSENT",
        "INVITATION",
        "PROVENANCE",
        "GENERATION_BUDGET_CANDIDATE_ARTIFACT",
        "PROOF_OPEN",
        "QUOTE",
        "EVIDENCE_REJECTION_OR_READINESS_REVOCATION",
        "DIAGNOSTIC_GRANT",
        "ADDITIONAL_RECONCILIATION_AND_RECOVERY",
    ],
});
export const customerOperationsRuntimeAuthorityBoundaryPolicy = Object.freeze({
    pureContractsAuthenticateBuilderCaller: false,
    serverContextRequiresRuntimeJwtMembershipAndPersistenceAuthority: true,
    providerContextRequiresRuntimeSignaturePollOrReconciliationVerification: true,
    awsServiceContextRequiresRuntimeSourceVerification: true,
    runtimeIamAndAuditRemainRequired: true,
});
export const customerOperationsEventDefinitions = deepFreeze({
    COGNITO_POST_CONFIRMATION_OBSERVED: defineEvent({
        family: "IDENTITY_AUTH",
        authority: "AWS_SERVICE",
        producers: awsCognito,
        payload: {
            kind: "IDENTITY",
            observationCode: "POST_CONFIRMATION",
            evidence: "BEST_EFFORT_COGNITO_TELEMETRY",
            atomicAuthenticationTruth: false,
        },
        allowedResults: observed,
        requiredSubjectIds: customer,
        aggregateType: null,
    }),
    COGNITO_POST_AUTHENTICATION_OBSERVED: defineEvent({
        family: "IDENTITY_AUTH",
        authority: "AWS_SERVICE",
        producers: awsCognito,
        payload: {
            kind: "IDENTITY",
            observationCode: "POST_AUTHENTICATION",
            evidence: "BEST_EFFORT_COGNITO_TELEMETRY",
            atomicAuthenticationTruth: false,
        },
        allowedResults: observed,
        requiredSubjectIds: customer,
        aggregateType: null,
    }),
    CLIENT_AUTH_FAILURE_OBSERVED: defineEvent({
        family: "IDENTITY_AUTH",
        authority: "CLIENT_OBSERVED",
        producers: serverApi,
        payload: { kind: "CLIENT_OBSERVATION", actionCode: "AUTH_FAILURE" },
        allowedResults: observed,
        requiredSubjectIds: workflowSubject,
        aggregateType: null,
    }),
    APP_PROFILE_CREATED: defineEvent({
        family: "IDENTITY_AUTH",
        authority: "SERVER",
        producers: workflow,
        payload: { kind: "IDENTITY", stateCode: "CREATED", evidence: "APPLICATION_TRANSACTION" },
        allowedResults: succeeded,
        requiredSubjectIds: customer,
        aggregateType: "PROFILE",
    }),
    APP_PROFILE_UPDATED: defineEvent({
        family: "IDENTITY_AUTH",
        authority: "SERVER",
        producers: serverApi,
        payload: { kind: "IDENTITY", stateCode: "UPDATED", evidence: "APPLICATION_TRANSACTION" },
        allowedResults: succeeded,
        requiredSubjectIds: customer,
        aggregateType: "PROFILE",
    }),
    APP_SESSION_ESTABLISHED: defineEvent({
        family: "IDENTITY_AUTH",
        authority: "SERVER",
        producers: serverApi,
        payload: {
            kind: "IDENTITY",
            stateCode: "SESSION_ESTABLISHED",
            evidence: "AUTHENTICATED_APPLICATION_REQUEST",
        },
        allowedResults: succeeded,
        requiredSubjectIds: customer,
        aggregateType: null,
    }),
    CHECKOUT_REQUESTED: defineEvent({
        family: "COMMERCE_GIFT",
        authority: "SERVER",
        producers: serverApi,
        payload: { kind: "COMMERCE", stateCode: "REQUESTED" },
        allowedResults: customerAccepted,
        requiredSubjectIds: customer,
        aggregateType: "CHECKOUT",
    }),
    PAYMENT_STATUS_CONFIRMED: defineEvent({
        family: "COMMERCE_GIFT",
        authority: "PROVIDER",
        producers: provider,
        payload: { kind: "COMMERCE", stateCode: "PAYMENT_CONFIRMED" },
        allowedResults: succeeded,
        requiredSubjectIds: customerOrder,
        aggregateType: "CHECKOUT",
    }),
    VOUCHER_REDEMPTION_ACCEPTED: defineEvent({
        family: "COMMERCE_GIFT",
        authority: "SERVER",
        producers: serverApi,
        payload: { kind: "COMMERCE", stateCode: "VOUCHER_ACCEPTED" },
        allowedResults: customerAccepted,
        requiredSubjectIds: customer,
        aggregateType: "VOUCHER",
    }),
    VOUCHER_REDEMPTION_REJECTED: defineEvent({
        family: "COMMERCE_GIFT",
        authority: "SERVER",
        producers: serverApi,
        payload: { kind: "COMMERCE", stateCode: "VOUCHER_REJECTED" },
        allowedResults: policyRejected,
        requiredSubjectIds: customer,
        aggregateType: "VOUCHER",
    }),
    BOOK_CLAIMED: defineEvent({
        family: "COMMERCE_GIFT",
        authority: "SERVER",
        producers: serverApi,
        payload: { kind: "COMMERCE", stateCode: "BOOK_CLAIMED" },
        allowedResults: succeeded,
        requiredSubjectIds: customerBook,
        aggregateType: "BOOK",
    }),
    REWARD_STATUS_CHANGED: defineEvent({
        family: "COMMERCE_GIFT",
        authority: "SERVER",
        producers: workflow,
        payload: { kind: "COMMERCE", stateCode: "REWARD_CHANGED" },
        allowedResults: succeeded,
        requiredSubjectIds: customer,
        aggregateType: "REWARD",
    }),
    BOOK_CREATED: defineEvent({
        family: "BOOK_COLLABORATION",
        authority: "SERVER",
        producers: serverApi,
        payload: { kind: "BOOK", stateCode: "CREATED" },
        allowedResults: succeeded,
        requiredSubjectIds: customerBook,
        aggregateType: "BOOK",
    }),
    BOOK_STATE_CHANGED: defineEvent({
        family: "BOOK_COLLABORATION",
        authority: "SERVER",
        producers: serverApi,
        payload: { kind: "BOOK", stateCode: "STATE_CHANGED" },
        allowedResults: succeeded,
        requiredSubjectIds: customerBook,
        aggregateType: "BOOK",
    }),
    BOOK_MEMBERSHIP_CHANGED: defineEvent({
        family: "BOOK_COLLABORATION",
        authority: "SERVER",
        producers: serverApi,
        payload: { kind: "BOOK", stateCode: "MEMBERSHIP_CHANGED" },
        allowedResults: succeeded,
        requiredSubjectIds: customerBook,
        aggregateType: "BOOK",
    }),
    BOOK_DELETION_REQUESTED: defineEvent({
        family: "BOOK_COLLABORATION",
        authority: "SERVER",
        producers: serverApi,
        payload: { kind: "BOOK", stateCode: "DELETION_REQUESTED" },
        allowedResults: customerAccepted,
        requiredSubjectIds: customerBook,
        aggregateType: "BOOK",
    }),
    BOOK_DELETION_CANCELLED: defineEvent({
        family: "BOOK_COLLABORATION",
        authority: "SERVER",
        producers: serverApi,
        payload: { kind: "BOOK", stateCode: "DELETION_CANCELLED" },
        allowedResults: succeeded,
        requiredSubjectIds: customerBook,
        aggregateType: "BOOK",
    }),
    BOOK_DELETION_COMPLETED: defineEvent({
        family: "BOOK_COLLABORATION",
        authority: "SERVER",
        producers: workflow,
        payload: { kind: "BOOK", stateCode: "DELETION_COMPLETED" },
        allowedResults: succeeded,
        requiredSubjectIds: customerBook,
        aggregateType: "BOOK",
    }),
    BOOK_EXPORT_REQUESTED: defineEvent({
        family: "BOOK_COLLABORATION",
        authority: "SERVER",
        producers: serverApi,
        payload: { kind: "BOOK", stateCode: "EXPORT_REQUESTED" },
        allowedResults: customerAccepted,
        requiredSubjectIds: customerBook,
        aggregateType: "EXPORT",
    }),
    BOOK_EXPORT_READY: defineEvent({
        family: "BOOK_COLLABORATION",
        authority: "SERVER",
        producers: workflow,
        payload: { kind: "BOOK", stateCode: "EXPORT_READY" },
        allowedResults: succeeded,
        requiredSubjectIds: customerBookArtifact,
        aggregateType: "EXPORT",
    }),
    SOURCE_CAPTURE_PERSISTED: defineEvent({
        family: "CAPTURE_MEDIA",
        authority: "SERVER",
        producers: serverApi,
        payload: { kind: "CAPTURE", stateCode: "PERSISTED" },
        allowedResults: succeeded,
        requiredSubjectIds: customerBookArtifact,
        aggregateType: "SOURCE",
    }),
    MEDIA_UPLOAD_QUARANTINED: defineEvent({
        family: "CAPTURE_MEDIA",
        authority: "SERVER",
        producers: serverApi,
        payload: { kind: "CAPTURE", stateCode: "QUARANTINED" },
        allowedResults: customerAccepted,
        requiredSubjectIds: customerBookArtifact,
        aggregateType: "SOURCE",
    }),
    MEDIA_UPLOAD_VALIDATED: defineEvent({
        family: "CAPTURE_MEDIA",
        authority: "SERVER",
        producers: workflow,
        payload: { kind: "CAPTURE", stateCode: "VALIDATED" },
        allowedResults: succeeded,
        requiredSubjectIds: customerBookArtifact,
        aggregateType: "SOURCE",
    }),
    MEDIA_UPLOAD_REJECTED: defineEvent({
        family: "CAPTURE_MEDIA",
        authority: "SERVER",
        producers: workflow,
        payload: { kind: "CAPTURE", stateCode: "REJECTED" },
        allowedResults: validationRejected,
        requiredSubjectIds: customerBookArtifact,
        aggregateType: "SOURCE",
    }),
    TRANSCRIPTION_STARTED: defineEvent({
        family: "CAPTURE_MEDIA",
        authority: "SERVER",
        producers: workflow,
        payload: { kind: "CAPTURE", stateCode: "TRANSCRIPTION_STARTED" },
        allowedResults: customerAccepted,
        requiredSubjectIds: customerBookArtifact,
        aggregateType: "SOURCE",
    }),
    TRANSCRIPTION_COMPLETED: defineEvent({
        family: "CAPTURE_MEDIA",
        authority: "SERVER",
        producers: workflow,
        payload: { kind: "CAPTURE", stateCode: "TRANSCRIPTION_COMPLETED" },
        allowedResults: succeeded,
        requiredSubjectIds: customerBookArtifact,
        aggregateType: "SOURCE",
    }),
    TRANSCRIPTION_FAILED: defineEvent({
        family: "CAPTURE_MEDIA",
        authority: "SERVER",
        producers: workflow,
        payload: { kind: "CAPTURE", stateCode: "TRANSCRIPTION_FAILED" },
        allowedResults: systemFailed,
        requiredSubjectIds: customerBookArtifact,
        aggregateType: "SOURCE",
    }),
    AWS_MEDIA_SCAN_COMPLETED: defineEvent({
        family: "CAPTURE_MEDIA",
        authority: "AWS_SERVICE",
        producers: awsNormalizer,
        payload: { kind: "CAPTURE", stateCode: "MEDIA_SCAN_COMPLETED" },
        allowedResults: observed,
        requiredSubjectIds: artifactSubject,
        aggregateType: "SOURCE",
    }),
    AWS_EMAIL_DELIVERY_STATUS_OBSERVED: defineEvent({
        family: "RELIABILITY",
        authority: "AWS_SERVICE",
        producers: awsNormalizer,
        payload: { kind: "RELIABILITY", recoveryCode: "EMAIL_DELIVERY_STATUS" },
        allowedResults: observed,
        requiredSubjectIds: workflowSubject,
        aggregateType: null,
    }),
    GENERATION_REQUESTED: defineEvent({
        family: "PRODUCTION",
        authority: "SERVER",
        producers: serverApi,
        payload: { kind: "PRODUCTION", stageCode: "REQUESTED" },
        allowedResults: customerAccepted,
        requiredSubjectIds: customerBookWorkflow,
        aggregateType: "GENERATION",
    }),
    GENERATION_STARTED: defineEvent({
        family: "PRODUCTION",
        authority: "SERVER",
        producers: workflow,
        payload: { kind: "PRODUCTION", stageCode: "STARTED" },
        allowedResults: customerAccepted,
        requiredSubjectIds: customerBookWorkflow,
        aggregateType: "GENERATION",
    }),
    GENERATION_STAGE_COMPLETED: defineEvent({
        family: "PRODUCTION",
        authority: "SERVER",
        producers: workflow,
        payload: { kind: "PRODUCTION", stageCode: "STAGE_COMPLETED" },
        allowedResults: succeeded,
        requiredSubjectIds: customerBookWorkflow,
        aggregateType: "GENERATION",
    }),
    GENERATION_STAGE_FAILED: defineEvent({
        family: "PRODUCTION",
        authority: "SERVER",
        producers: workflow,
        payload: { kind: "PRODUCTION", stageCode: "STAGE_FAILED" },
        allowedResults: systemFailed,
        requiredSubjectIds: customerBookWorkflow,
        aggregateType: "GENERATION",
    }),
    GENERATION_COMPLETED: defineEvent({
        family: "PRODUCTION",
        authority: "SERVER",
        producers: workflow,
        payload: { kind: "PRODUCTION", stageCode: "COMPLETED" },
        allowedResults: succeeded,
        requiredSubjectIds: customerBookWorkflow,
        aggregateType: "GENERATION",
    }),
    GENERATION_BUDGET_RECONCILED: defineEvent({
        family: "PRODUCTION",
        authority: "SERVER",
        producers: workflow,
        payload: { kind: "PRODUCTION", stageCode: "BUDGET_RECONCILED" },
        allowedResults: recovered,
        requiredSubjectIds: customerBookWorkflow,
        aggregateType: "GENERATION",
    }),
    REVISION_SAVED: defineEvent({
        family: "REVIEW_OUTPUT",
        authority: "SERVER",
        producers: serverApi,
        payload: { kind: "REVIEW_OUTPUT", stateCode: "REVISION_SAVED" },
        allowedResults: succeeded,
        requiredSubjectIds: customerBook,
        aggregateType: "REVISION",
    }),
    REVISION_CONFLICTED: defineEvent({
        family: "REVIEW_OUTPUT",
        authority: "SERVER",
        producers: serverApi,
        payload: { kind: "REVIEW_OUTPUT", stateCode: "REVISION_CONFLICTED" },
        allowedResults: conflictRejected,
        requiredSubjectIds: customerBook,
        aggregateType: "REVISION",
    }),
    REVISION_RECOVERED: defineEvent({
        family: "REVIEW_OUTPUT",
        authority: "SERVER",
        producers: serverApi,
        payload: { kind: "REVIEW_OUTPUT", stateCode: "REVISION_RECOVERED" },
        allowedResults: recovered,
        requiredSubjectIds: customerBook,
        aggregateType: "REVISION",
    }),
    APPROVAL_REQUESTED: defineEvent({
        family: "REVIEW_OUTPUT",
        authority: "SERVER",
        producers: serverApi,
        payload: { kind: "REVIEW_OUTPUT", stateCode: "APPROVAL_REQUESTED" },
        allowedResults: customerAccepted,
        requiredSubjectIds: customerBook,
        aggregateType: "REVISION",
    }),
    APPROVAL_GRANTED: defineEvent({
        family: "REVIEW_OUTPUT",
        authority: "SERVER",
        producers: serverApi,
        payload: { kind: "REVIEW_OUTPUT", stateCode: "APPROVAL_GRANTED" },
        allowedResults: succeeded,
        requiredSubjectIds: customerBook,
        aggregateType: "REVISION",
    }),
    APPROVAL_INVALIDATED: defineEvent({
        family: "REVIEW_OUTPUT",
        authority: "SERVER",
        producers: workflow,
        payload: { kind: "REVIEW_OUTPUT", stateCode: "APPROVAL_INVALIDATED" },
        allowedResults: succeeded,
        requiredSubjectIds: customerBook,
        aggregateType: "REVISION",
    }),
    PREFLIGHT_PASSED: defineEvent({
        family: "REVIEW_OUTPUT",
        authority: "SERVER",
        producers: workflow,
        payload: { kind: "REVIEW_OUTPUT", stateCode: "PREFLIGHT_PASSED" },
        allowedResults: succeeded,
        requiredSubjectIds: customerBookArtifact,
        aggregateType: "EXPORT",
    }),
    PREFLIGHT_FAILED: defineEvent({
        family: "REVIEW_OUTPUT",
        authority: "SERVER",
        producers: workflow,
        payload: { kind: "REVIEW_OUTPUT", stateCode: "PREFLIGHT_FAILED" },
        allowedResults: validationFailed,
        requiredSubjectIds: customerBookArtifact,
        aggregateType: "EXPORT",
    }),
    EXPORT_READY: defineEvent({
        family: "REVIEW_OUTPUT",
        authority: "SERVER",
        producers: workflow,
        payload: { kind: "REVIEW_OUTPUT", stateCode: "EXPORT_READY" },
        allowedResults: succeeded,
        requiredSubjectIds: customerBookArtifact,
        aggregateType: "EXPORT",
    }),
    EXPORT_FAILED: defineEvent({
        family: "REVIEW_OUTPUT",
        authority: "SERVER",
        producers: workflow,
        payload: { kind: "REVIEW_OUTPUT", stateCode: "EXPORT_FAILED" },
        allowedResults: systemFailed,
        requiredSubjectIds: customerBook,
        aggregateType: "EXPORT",
    }),
    BOOK_PRODUCTION_EVIDENCE_VERIFIED: defineEvent({
        family: "LC97_EVIDENCE",
        authority: "SERVER",
        producers: workflow,
        payload: { kind: "LC97_EVIDENCE", stateCode: "VERIFIED", evidenceVersion: "V1" },
        allowedResults: succeeded,
        requiredSubjectIds: customerBookArtifact,
        aggregateType: "PRODUCTION_EVIDENCE",
    }),
    PRINT_PACKAGE_ELIGIBILITY_ESTABLISHED: defineEvent({
        family: "LC97_EVIDENCE",
        authority: "SERVER",
        producers: workflow,
        payload: { kind: "LC97_EVIDENCE", stateCode: "PRINT_ELIGIBLE", evidenceVersion: "V1" },
        allowedResults: succeeded,
        requiredSubjectIds: customerBookArtifact,
        aggregateType: "PRODUCTION_EVIDENCE",
    }),
    PRINT_AUTHORIZATION_GRANTED: defineEvent({
        family: "PRINT_FULFILLMENT",
        authority: "SERVER",
        producers: serverApi,
        payload: { kind: "PRINT_FULFILLMENT", stateCode: "AUTHORIZED" },
        allowedResults: succeeded,
        requiredSubjectIds: customerBook,
        aggregateType: "PRINT_AUTHORIZATION",
    }),
    PRINT_AUTHORIZATION_REVOKED: defineEvent({
        family: "PRINT_FULFILLMENT",
        authority: "SERVER",
        producers: serverApi,
        payload: { kind: "PRINT_FULFILLMENT", stateCode: "REVOKED" },
        allowedResults: succeeded,
        requiredSubjectIds: customerBook,
        aggregateType: "PRINT_AUTHORIZATION",
    }),
    PROVIDER_ORDER_SUBMISSION_ACCEPTED: defineEvent({
        family: "PRINT_FULFILLMENT",
        authority: "PROVIDER",
        producers: provider,
        payload: { kind: "PRINT_FULFILLMENT", stateCode: "SUBMISSION_ACCEPTED" },
        allowedResults: providerAccepted,
        requiredSubjectIds: customerOrder,
        aggregateType: "ORDER",
    }),
    PROVIDER_ORDER_STATUS_CONFIRMED: defineEvent({
        family: "PRINT_FULFILLMENT",
        authority: "PROVIDER",
        producers: provider,
        payload: { kind: "PRINT_FULFILLMENT", stateCode: "ORDER_STATUS_CONFIRMED" },
        allowedResults: succeeded,
        requiredSubjectIds: customerOrder,
        aggregateType: "ORDER",
    }),
    SHIPMENT_STATUS_CONFIRMED: defineEvent({
        family: "PRINT_FULFILLMENT",
        authority: "PROVIDER",
        producers: provider,
        payload: { kind: "PRINT_FULFILLMENT", stateCode: "SHIPPED" },
        allowedResults: succeeded,
        requiredSubjectIds: customerOrder,
        aggregateType: "FULFILLMENT",
    }),
    DELIVERY_STATUS_CONFIRMED: defineEvent({
        family: "PRINT_FULFILLMENT",
        authority: "PROVIDER",
        producers: provider,
        payload: { kind: "PRINT_FULFILLMENT", stateCode: "DELIVERED" },
        allowedResults: succeeded,
        requiredSubjectIds: customerOrder,
        aggregateType: "FULFILLMENT",
    }),
    FULFILLMENT_EXCEPTION_CONFIRMED: defineEvent({
        family: "PRINT_FULFILLMENT",
        authority: "PROVIDER",
        producers: provider,
        payload: { kind: "PRINT_FULFILLMENT", stateCode: "EXCEPTION_CONFIRMED" },
        allowedResults: providerFailed,
        requiredSubjectIds: customerOrder,
        aggregateType: "FULFILLMENT",
    }),
    FULFILLMENT_RECONCILED: defineEvent({
        family: "PRINT_FULFILLMENT",
        authority: "SERVER",
        producers: recovery,
        payload: { kind: "PRINT_FULFILLMENT", stateCode: "RECONCILED" },
        allowedResults: recovered,
        requiredSubjectIds: customerOrder,
        aggregateType: "FULFILLMENT",
    }),
    PROVIDER_WEBHOOK_HINT_OBSERVED: defineEvent({
        family: "PRINT_FULFILLMENT",
        authority: "SERVER",
        producers: ["PROVIDER_WEBHOOK"],
        payload: { kind: "PRINT_FULFILLMENT", stateCode: "WEBHOOK_HINT_OBSERVED" },
        allowedResults: observed,
        requiredSubjectIds: customerOrder,
        aggregateType: "FULFILLMENT",
    }),
    SUPPORT_TICKET_RECEIVED: defineEvent({
        family: "SUPPORT",
        authority: "SERVER",
        producers: ["SUPPORT_API"],
        payload: { kind: "SUPPORT", stateCode: "RECEIVED" },
        allowedResults: customerAccepted,
        requiredSubjectIds: ticket,
        aggregateType: "SUPPORT_TICKET",
    }),
    SUPPORT_TICKET_ASSIGNED: defineEvent({
        family: "SUPPORT",
        authority: "SERVER",
        producers: supportApi,
        payload: { kind: "SUPPORT", stateCode: "ASSIGNED" },
        allowedResults: succeeded,
        requiredSubjectIds: ticket,
        aggregateType: "SUPPORT_TICKET",
    }),
    SUPPORT_REVIEW_STARTED: defineEvent({
        family: "SUPPORT",
        authority: "SERVER",
        producers: supportApi,
        payload: { kind: "SUPPORT", stateCode: "IN_REVIEW" },
        allowedResults: succeeded,
        requiredSubjectIds: ticket,
        aggregateType: "SUPPORT_TICKET",
    }),
    SUPPORT_WAITING_FOR_CUSTOMER: defineEvent({
        family: "SUPPORT",
        authority: "SERVER",
        producers: supportApi,
        payload: { kind: "SUPPORT", stateCode: "WAITING_FOR_YOU" },
        allowedResults: succeeded,
        requiredSubjectIds: ticket,
        aggregateType: "SUPPORT_TICKET",
    }),
    SUPPORT_RESPONSE_RECORDED: defineEvent({
        family: "SUPPORT",
        authority: "SERVER",
        producers: supportApi,
        payload: { kind: "SUPPORT", stateCode: "RESPONSE_RECORDED" },
        allowedResults: succeeded,
        requiredSubjectIds: ticket,
        aggregateType: "SUPPORT_TICKET",
    }),
    SUPPORT_TICKET_RESOLVED: defineEvent({
        family: "SUPPORT",
        authority: "SERVER",
        producers: supportApi,
        payload: { kind: "SUPPORT", stateCode: "RESOLVED" },
        allowedResults: succeeded,
        requiredSubjectIds: ticket,
        aggregateType: "SUPPORT_TICKET",
    }),
    SUPPORT_TICKET_REOPENED: defineEvent({
        family: "SUPPORT",
        authority: "SERVER",
        producers: supportApi,
        payload: { kind: "SUPPORT", stateCode: "REOPENED" },
        allowedResults: succeeded,
        requiredSubjectIds: ticket,
        aggregateType: "SUPPORT_TICKET",
    }),
    SUPPORT_BODY_DELETED: defineEvent({
        family: "SUPPORT",
        authority: "SERVER",
        producers: recovery,
        payload: { kind: "SUPPORT", stateCode: "BODY_DELETED" },
        allowedResults: succeeded,
        requiredSubjectIds: ticket,
        aggregateType: "SUPPORT_TICKET",
    }),
    SUPPORT_DIAGNOSTIC_ACCESS_VIEWED: defineEvent({
        family: "SUPPORT",
        authority: "SERVER",
        producers: supportApi,
        payload: { kind: "SUPPORT", stateCode: "DIAGNOSTIC_ACCESS_VIEWED" },
        allowedResults: observed,
        requiredSubjectIds: ticket,
        aggregateType: "SUPPORT_TICKET",
    }),
    OUTBOX_PUBLICATION_RETRY_SCHEDULED: defineEvent({
        family: "RELIABILITY",
        authority: "SERVER",
        producers: recovery,
        payload: { kind: "RELIABILITY", recoveryCode: "OUTBOX_RETRY_SCHEDULED" },
        allowedResults: scheduled,
        requiredSubjectIds: artifactSubject,
        aggregateType: "OUTBOX",
    }),
    OUTBOX_PUBLICATION_RECOVERED: defineEvent({
        family: "RELIABILITY",
        authority: "SERVER",
        producers: recovery,
        payload: { kind: "RELIABILITY", recoveryCode: "OUTBOX_RECOVERED" },
        allowedResults: recovered,
        requiredSubjectIds: artifactSubject,
        aggregateType: "OUTBOX",
    }),
    EVENT_TARGET_DEAD_LETTERED: defineEvent({
        family: "RELIABILITY",
        authority: "SERVER",
        producers: recovery,
        payload: { kind: "RELIABILITY", recoveryCode: "TARGET_DEAD_LETTERED" },
        allowedResults: deadLetterFailed,
        requiredSubjectIds: artifactSubject,
        aggregateType: "OUTBOX",
    }),
    EVENT_REPLAY_STARTED: defineEvent({
        family: "RELIABILITY",
        authority: "SERVER",
        producers: recovery,
        payload: { kind: "RELIABILITY", recoveryCode: "REPLAY_STARTED" },
        allowedResults: customerAccepted,
        requiredSubjectIds: workflowSubject,
        aggregateType: "RECONCILIATION",
    }),
    EVENT_REPLAY_COMPLETED: defineEvent({
        family: "RELIABILITY",
        authority: "SERVER",
        producers: recovery,
        payload: { kind: "RELIABILITY", recoveryCode: "REPLAY_COMPLETED" },
        allowedResults: recovered,
        requiredSubjectIds: workflowSubject,
        aggregateType: "RECONCILIATION",
    }),
    PROJECTION_VERSION_CONFLICT_DETECTED: defineEvent({
        family: "RELIABILITY",
        authority: "SERVER",
        producers: recovery,
        payload: { kind: "RELIABILITY", recoveryCode: "PROJECTION_VERSION_CONFLICT" },
        allowedResults: conflictFailed,
        requiredSubjectIds: artifactSubject,
        aggregateType: "PROJECTION",
    }),
    PROJECTION_RECOVERED: defineEvent({
        family: "RELIABILITY",
        authority: "SERVER",
        producers: recovery,
        payload: { kind: "RELIABILITY", recoveryCode: "PROJECTION_RECOVERED" },
        allowedResults: recovered,
        requiredSubjectIds: artifactSubject,
        aggregateType: "PROJECTION",
    }),
    RECONCILIATION_COMPLETED: defineEvent({
        family: "RELIABILITY",
        authority: "SERVER",
        producers: recovery,
        payload: { kind: "RELIABILITY", recoveryCode: "RECONCILIATION_COMPLETED" },
        allowedResults: recovered,
        requiredSubjectIds: workflowSubject,
        aggregateType: "RECONCILIATION",
    }),
    CLIENT_ROUTE_VIEWED: defineEvent({
        family: "CLIENT_OBSERVED",
        authority: "CLIENT_OBSERVED",
        producers: serverApi,
        payload: { kind: "CLIENT_OBSERVATION", actionCode: "ROUTE_VIEWED" },
        allowedResults: observed,
        requiredSubjectIds: customer,
        aggregateType: null,
    }),
    CLIENT_SIGN_OUT_SELECTED: defineEvent({
        family: "CLIENT_OBSERVED",
        authority: "CLIENT_OBSERVED",
        producers: serverApi,
        payload: { kind: "CLIENT_OBSERVATION", actionCode: "SIGN_OUT_SELECTED" },
        allowedResults: observed,
        requiredSubjectIds: customer,
        aggregateType: null,
    }),
    STUDIO_SESSION_STARTED: defineEvent({
        family: "CLIENT_OBSERVED",
        authority: "CLIENT_OBSERVED",
        producers: serverApi,
        payload: { kind: "CLIENT_OBSERVATION", actionCode: "STUDIO_SESSION_STARTED" },
        allowedResults: observed,
        requiredSubjectIds: customerBook,
        aggregateType: null,
    }),
    CLIENT_ACTION_SELECTED: defineEvent({
        family: "CLIENT_OBSERVED",
        authority: "CLIENT_OBSERVED",
        producers: serverApi,
        payload: { kind: "CLIENT_OBSERVATION", actionCode: "ACTION_SELECTED" },
        allowedResults: observed,
        requiredSubjectIds: customer,
        aggregateType: null,
    }),
    CLIENT_FORM_ATTEMPTED: defineEvent({
        family: "CLIENT_OBSERVED",
        authority: "CLIENT_OBSERVED",
        producers: serverApi,
        payload: { kind: "CLIENT_OBSERVATION", actionCode: "FORM_ATTEMPTED" },
        allowedResults: observed,
        requiredSubjectIds: customer,
        aggregateType: null,
    }),
    CLIENT_VALIDATION_ERROR_OBSERVED: defineEvent({
        family: "CLIENT_OBSERVED",
        authority: "CLIENT_OBSERVED",
        producers: serverApi,
        payload: { kind: "CLIENT_OBSERVATION", actionCode: "VALIDATION_ERROR" },
        allowedResults: observed,
        requiredSubjectIds: customer,
        aggregateType: null,
    }),
    CLIENT_NETWORK_ERROR_OBSERVED: defineEvent({
        family: "CLIENT_OBSERVED",
        authority: "CLIENT_OBSERVED",
        producers: serverApi,
        payload: { kind: "CLIENT_OBSERVATION", actionCode: "NETWORK_ERROR" },
        allowedResults: observed,
        requiredSubjectIds: customer,
        aggregateType: null,
    }),
    CLIENT_RECOVERY_SELECTED: defineEvent({
        family: "CLIENT_OBSERVED",
        authority: "CLIENT_OBSERVED",
        producers: serverApi,
        payload: { kind: "CLIENT_OBSERVATION", actionCode: "RECOVERY_SELECTED" },
        allowedResults: observed,
        requiredSubjectIds: customer,
        aggregateType: null,
    }),
});
const eventTypeValues = Object.keys(customerOperationsEventDefinitions);
export const customerOperationsEventTypeSchema = z.enum(eventTypeValues);
/**
 * Catalog types nothing sends yet: measured 2026-10-16 (PR 1b) as named nowhere in the services,
 * web or admin code. They stay in the closed, versioned catalog, but no reader should take them
 * for live evidence. A type leaves this list in the change that first sends it; a test keeps the
 * two in step. Most of what they describe is now captured by the journey record instead.
 * Completion 2 adds a gated durable-source bridge for refusal/generation/review-PDF facts;
 * their code availability is not proof of capture completeness or deployment.
 */
export const reservedCustomerOperationsEventTypes = Object.freeze([
    "COGNITO_POST_CONFIRMATION_OBSERVED",
    "COGNITO_POST_AUTHENTICATION_OBSERVED",
    "CLIENT_AUTH_FAILURE_OBSERVED",
    "APP_PROFILE_UPDATED",
    "CHECKOUT_REQUESTED",
    "PAYMENT_STATUS_CONFIRMED",
    "REWARD_STATUS_CHANGED",
    "BOOK_STATE_CHANGED",
    "BOOK_MEMBERSHIP_CHANGED",
    "BOOK_DELETION_COMPLETED",
    "BOOK_EXPORT_REQUESTED",
    "BOOK_EXPORT_READY",
    "SOURCE_CAPTURE_PERSISTED",
    "MEDIA_UPLOAD_QUARANTINED",
    "MEDIA_UPLOAD_VALIDATED",
    "MEDIA_UPLOAD_REJECTED",
    "TRANSCRIPTION_STARTED",
    "TRANSCRIPTION_COMPLETED",
    "TRANSCRIPTION_FAILED",
    "AWS_MEDIA_SCAN_COMPLETED",
    "AWS_EMAIL_DELIVERY_STATUS_OBSERVED",
    "GENERATION_STARTED",
    "GENERATION_STAGE_COMPLETED",
    "GENERATION_BUDGET_RECONCILED",
    "REVISION_SAVED",
    "REVISION_CONFLICTED",
    "REVISION_RECOVERED",
    "APPROVAL_INVALIDATED",
    "PREFLIGHT_PASSED",
    "BOOK_PRODUCTION_EVIDENCE_VERIFIED",
    "PRINT_PACKAGE_ELIGIBILITY_ESTABLISHED",
    "PRINT_AUTHORIZATION_REVOKED",
    "PROVIDER_ORDER_STATUS_CONFIRMED",
    "FULFILLMENT_RECONCILED",
    "PROVIDER_WEBHOOK_HINT_OBSERVED",
    "SUPPORT_REVIEW_STARTED",
    "SUPPORT_WAITING_FOR_CUSTOMER",
    "SUPPORT_RESPONSE_RECORDED",
    "SUPPORT_BODY_DELETED",
    "SUPPORT_DIAGNOSTIC_ACCESS_VIEWED",
    "OUTBOX_PUBLICATION_RETRY_SCHEDULED",
    "OUTBOX_PUBLICATION_RECOVERED",
    "EVENT_TARGET_DEAD_LETTERED",
    "EVENT_REPLAY_STARTED",
    "EVENT_REPLAY_COMPLETED",
    "PROJECTION_VERSION_CONFLICT_DETECTED",
    "PROJECTION_RECOVERED",
    "RECONCILIATION_COMPLETED",
    "CLIENT_ROUTE_VIEWED",
    "CLIENT_ACTION_SELECTED",
    "CLIENT_FORM_ATTEMPTED",
    "CLIENT_VALIDATION_ERROR_OBSERVED",
    "CLIENT_NETWORK_ERROR_OBSERVED",
    "CLIENT_RECOVERY_SELECTED",
]);
export const customerOperationsEventFamilyByType = Object.freeze(Object.fromEntries(eventTypeValues.map((eventType) => [
    eventType,
    customerOperationsEventDefinitions[eventType].family,
])));
export const customerOperationsAuthorityByType = Object.freeze(Object.fromEntries(eventTypeValues.map((eventType) => [
    eventType,
    customerOperationsEventDefinitions[eventType].authority,
])));
const payloadValueSchema = z.union([z.string(), z.number(), z.boolean()]);
const customerOperationsEventPayloadSchema = z.record(z.string(), payloadValueSchema);
const eventResultSchema = z
    .object({
    outcome: customerOperationsOutcomeSchema.optional(),
    reasonCode: customerOperationsReasonCodeSchema.optional(),
})
    .strict();
const constructionEnvelopeContextFields = {
    eventId: customerOperationsEventIdSchema,
    occurredAt: z.string().datetime(),
    stage: customerOperationsStageSchema,
    test: z.boolean(),
    correlationId: customerOperationsCorrelationIdSchema.optional(),
    causationId: customerOperationsCausationIdSchema.optional(),
    subject: customerOperationsSubjectSchema,
    aggregate: customerOperationsAggregateSchema.optional(),
};
export const serverEventConstructionContextSchema = z
    .object({
    ...constructionEnvelopeContextFields,
    boundary: z.literal("SERVER_CONTEXT_REQUIRES_RUNTIME_AUTHORITY"),
    producer: customerOperationsProducerSchema,
})
    .strict();
export const providerEventConstructionContextSchema = z.discriminatedUnion("producer", [
    z
        .object({
        ...constructionEnvelopeContextFields,
        boundary: z.literal("PROVIDER_CONTEXT_REQUIRES_RUNTIME_VERIFICATION"),
        producer: z.literal("PROVIDER_WEBHOOK"),
        requiredRuntimeVerification: z
            .object({
            required: z.literal(true),
            method: z.literal("SIGNATURE_AND_FETCH"),
        })
            .strict(),
    })
        .strict(),
    z
        .object({
        ...constructionEnvelopeContextFields,
        boundary: z.literal("PROVIDER_CONTEXT_REQUIRES_RUNTIME_VERIFICATION"),
        producer: z.literal("PROVIDER_RECONCILER"),
        requiredRuntimeVerification: z
            .object({
            required: z.literal(true),
            method: z.literal("RECONCILER_FETCH"),
        })
            .strict(),
    })
        .strict(),
]);
export const awsServiceEventConstructionContextSchema = z
    .object({
    ...constructionEnvelopeContextFields,
    boundary: z.literal("AWS_SERVICE_CONTEXT_REQUIRES_RUNTIME_SOURCE_VERIFICATION"),
    producer: z.enum(["COGNITO_TRIGGER", "AWS_EVENT_NORMALIZER"]),
})
    .strict();
export const clientObservationConstructionContextSchema = z
    .object({
    ...constructionEnvelopeContextFields,
    boundary: z.literal("CLIENT_OBSERVATION_CONTEXT_REQUIRES_RUNTIME_AUTH"),
    producer: z.literal("CUSTOMER_API"),
    aggregate: z.undefined().optional(),
})
    .strict();
const serverEventTypes = eventTypeValues.filter((eventType) => customerOperationsEventDefinitions[eventType].authority === "SERVER");
const providerEventTypes = eventTypeValues.filter((eventType) => customerOperationsEventDefinitions[eventType].authority === "PROVIDER");
const awsEventTypes = eventTypeValues.filter((eventType) => customerOperationsEventDefinitions[eventType].authority === "AWS_SERVICE");
const clientEventTypes = eventTypeValues.filter((eventType) => customerOperationsEventDefinitions[eventType].authority === "CLIENT_OBSERVED");
export const serverEventConstructionCommandSchema = z
    .object({ eventType: z.enum(serverEventTypes), result: eventResultSchema.optional() })
    .strict();
export const providerEventConstructionCommandSchema = z
    .object({ eventType: z.enum(providerEventTypes), result: eventResultSchema.optional() })
    .strict();
export const awsServiceEventConstructionCommandSchema = z
    .object({ eventType: z.enum(awsEventTypes), result: eventResultSchema.optional() })
    .strict();
export const customerOperationsClientObservationCommandSchema = z
    .object({ eventType: z.enum(clientEventTypes) })
    .strict();
export function parseCustomerOperationsClientObservation(value) {
    return customerOperationsClientObservationCommandSchema.parse(value);
}
const completedEventFields = {
    schemaVersion: customerOperationsSchemaVersionSchema,
    catalogVersion: z.literal("LC100_OPERATIONS_PARTIAL_V1"),
    eventId: customerOperationsEventIdSchema,
    eventType: customerOperationsEventTypeSchema,
    eventFamily: z.enum([
        "IDENTITY_AUTH",
        "COMMERCE_GIFT",
        "BOOK_COLLABORATION",
        "CAPTURE_MEDIA",
        "PRODUCTION",
        "REVIEW_OUTPUT",
        "LC97_EVIDENCE",
        "PRINT_FULFILLMENT",
        "SUPPORT",
        "RELIABILITY",
        "CLIENT_OBSERVED",
    ]),
    authority: z.enum(["SERVER", "CLIENT_OBSERVED", "PROVIDER", "AWS_SERVICE"]),
    producer: customerOperationsProducerSchema,
    stage: customerOperationsStageSchema,
    test: z.boolean(),
    occurredAt: z.string().datetime(),
    outcome: customerOperationsOutcomeSchema,
    reasonCode: customerOperationsReasonCodeSchema.optional(),
    correlationId: customerOperationsCorrelationIdSchema.optional(),
    causationId: customerOperationsCausationIdSchema.optional(),
    subject: customerOperationsSubjectSchema,
    aggregate: customerOperationsAggregateSchema.optional(),
    payload: customerOperationsEventPayloadSchema,
};
function samePayload(actual, expected) {
    const actualKeys = Object.keys(actual).sort();
    const expectedKeys = Object.keys(expected).sort();
    return (actualKeys.length === expectedKeys.length &&
        actualKeys.every((key, index) => key === expectedKeys[index] && actual[key] === expected[key]));
}
function resultIsAllowed(definition, outcome, reasonCode) {
    return definition.allowedResults.some((allowed) => allowed.outcome === outcome &&
        (reasonCode === undefined ? allowed.allowNoReason : allowed.reasonCodes.includes(reasonCode)));
}
const customerOperationsEventValidationSchema = z
    .object(completedEventFields)
    .strict()
    .superRefine((event, context) => {
    const definition = customerOperationsEventDefinitions[event.eventType];
    if (event.eventFamily !== definition.family)
        context.addIssue({
            code: "custom",
            path: ["eventFamily"],
            message: "Event family mismatch.",
        });
    if (event.authority !== definition.authority)
        context.addIssue({
            code: "custom",
            path: ["authority"],
            message: "Event authority mismatch.",
        });
    if (!definition.producers.includes(event.producer))
        context.addIssue({ code: "custom", path: ["producer"], message: "Producer is not allowed." });
    if (!samePayload(event.payload, definition.payload))
        context.addIssue({ code: "custom", path: ["payload"], message: "Exact payload mismatch." });
    if (!resultIsAllowed(definition, event.outcome, event.reasonCode))
        context.addIssue({ code: "custom", path: ["outcome"], message: "Result is not allowed." });
    for (const identifier of definition.requiredSubjectIds)
        if (!event.subject[identifier])
            context.addIssue({
                code: "custom",
                path: ["subject", identifier],
                message: "Required authoritative subject identifier is missing.",
            });
    for (const identifier of Object.keys(event.subject))
        if (!definition.requiredSubjectIds.includes(identifier))
            context.addIssue({
                code: "custom",
                path: ["subject", identifier],
                message: "Subject identifier is not allowed for this event.",
            });
    if (definition.aggregateType === null && event.aggregate)
        context.addIssue({
            code: "custom",
            path: ["aggregate"],
            message: "Aggregate is not allowed.",
        });
    if (definition.aggregateType !== null && event.aggregate?.type !== definition.aggregateType)
        context.addIssue({
            code: "custom",
            path: ["aggregate", "type"],
            message: "Required aggregate type is missing or incorrect.",
        });
    if (definition.aggregateIdSource !== null &&
        event.aggregate &&
        event.aggregate.id !== event.subject[definition.aggregateIdSource])
        context.addIssue({
            code: "custom",
            path: ["aggregate", "id"],
            message: "Aggregate identifier must match its authoritative subject identifier.",
        });
    if (customerOperationsCanonicalEnvelopeUtf8Bytes(event) > MAX_CUSTOMER_OPERATIONS_EVENT_BYTES)
        context.addIssue({
            code: "custom",
            path: [],
            message: "CUSTOMER_OPERATIONS_EVENT_TOO_LARGE",
        });
});
export const customerOperationsEventSchema = customerOperationsEventValidationSchema.transform((event) => deepFreeze(event));
export const MAX_CUSTOMER_OPERATIONS_EVENT_BYTES = 16 * 1_024;
export const customerOperationsEnvelopeSizePolicy = Object.freeze({
    unit: "CANONICAL_UTF8_BYTES",
    maximum: MAX_CUSTOMER_OPERATIONS_EVENT_BYTES,
});
function canonicalJson(value) {
    if (value === null || typeof value === "string" || typeof value === "boolean")
        return JSON.stringify(value);
    if (typeof value === "number") {
        if (!Number.isFinite(value))
            throw new Error("CUSTOMER_OPERATIONS_CANONICAL_JSON_INVALID");
        return JSON.stringify(value);
    }
    if (Array.isArray(value))
        return `[${value.map(canonicalJson).join(",")}]`;
    if (typeof value === "object") {
        const record = value;
        return `{${Object.keys(record)
            .filter((key) => record[key] !== undefined)
            .sort()
            .map((key) => `${JSON.stringify(key)}:${canonicalJson(record[key])}`)
            .join(",")}}`;
    }
    throw new Error("CUSTOMER_OPERATIONS_CANONICAL_JSON_INVALID");
}
export function customerOperationsCanonicalEnvelopeUtf8Bytes(value) {
    return new TextEncoder().encode(canonicalJson(value)).byteLength;
}
export function assertCustomerOperationsEnvelopeSize(value) {
    if (customerOperationsCanonicalEnvelopeUtf8Bytes(value) > MAX_CUSTOMER_OPERATIONS_EVENT_BYTES)
        throw new Error("CUSTOMER_OPERATIONS_EVENT_TOO_LARGE");
}
function constructEvent(eventType, resultValue, contextValue) {
    const definition = customerOperationsEventDefinitions[eventType];
    const requestedResult = eventResultSchema.parse(resultValue ?? {});
    const firstResult = definition.allowedResults[0];
    const event = customerOperationsEventSchema.parse({
        schemaVersion: "2026-09-25.1",
        catalogVersion: customerOperationsCatalogIdentity.catalogVersion,
        eventId: contextValue.eventId,
        eventType,
        eventFamily: definition.family,
        authority: definition.authority,
        producer: contextValue.producer,
        stage: contextValue.stage,
        test: contextValue.test,
        occurredAt: contextValue.occurredAt,
        outcome: requestedResult.outcome ?? firstResult.outcome,
        reasonCode: requestedResult.reasonCode ??
            (firstResult.allowNoReason ? undefined : firstResult.reasonCodes[0]),
        correlationId: contextValue.correlationId,
        causationId: contextValue.causationId,
        subject: contextValue.subject,
        aggregate: contextValue.aggregate,
        payload: definition.payload,
    });
    assertCustomerOperationsEnvelopeSize(event);
    return event;
}
/** Construction constraint only; the future runtime adapter must authenticate and authorize it. */
export function buildServerContextCustomerOperationsEvent(commandValue, contextValue) {
    const command = serverEventConstructionCommandSchema.parse(commandValue);
    const context = serverEventConstructionContextSchema.parse(contextValue);
    return constructEvent(command.eventType, command.result, context);
}
/** Construction constraint only; it does not perform provider verification or reconciliation. */
export function buildProviderContextCustomerOperationsEvent(commandValue, contextValue) {
    const command = providerEventConstructionCommandSchema.parse(commandValue);
    const context = providerEventConstructionContextSchema.parse(contextValue);
    return constructEvent(command.eventType, command.result, context);
}
/** Construction constraint only; it does not authenticate the AWS event source. */
export function buildAwsServiceContextCustomerOperationsEvent(commandValue, contextValue) {
    const command = awsServiceEventConstructionCommandSchema.parse(commandValue);
    const context = awsServiceEventConstructionContextSchema.parse(contextValue);
    return constructEvent(command.eventType, command.result, context);
}
/** Restricted observation construction; runtime authentication and subject resolution remain external. */
export function buildCustomerOperationsClientObservation(commandValue, contextValue) {
    const command = customerOperationsClientObservationCommandSchema.parse(commandValue);
    const context = clientObservationConstructionContextSchema.parse(contextValue);
    return constructEvent(command.eventType, undefined, context);
}
/** Revalidates a possibly deserialized or copied event immediately before a future publish call. */
export function parseCustomerOperationsEventForPublish(value) {
    return customerOperationsEventSchema.parse(value);
}
//# sourceMappingURL=customer-operations-events.js.map